<template>
    <div>
        <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex gap-3">
                <input-field v-model="jobName" type="select" label="Select a Job" :options="jobNameOptions"
                    :toolTip="'Select a job to visualize its output data'" />
                <input-field v-model="tableName" type="select" label="Select a Table" :options="tableNameOptions"
                    :toolTip="'Select an output table'" />
                <div class="form-group">
                    <label for="limit" class="form-label">
                        Limit
                        <span class="fa fa-circle-info text-muted" data-bs-toggle="tooltip" data-bs-placement="right" title="Limit the number of rows to visualize"></span>
                    </label>
                    <input v-model="limit" type="number" class="form-control" placeholder="Select limit" id="limit" min="1" />
                </div>
            </div>
            <div>
                <div class="btn-group mt-2 pt-1" role="group" aria-label="Basic radio toggle button group">
                    <button class="btn" :class="tableView ? 'btn-primary' : 'btn-default btn-light'"
                        @click="toggleTableView(true)">
                        <i class="fa-solid fa-table"></i>
                    </button>
                    <button class="btn" :class="!tableView ? 'btn-primary' : 'btn-default btn-light'"
                        @click="toggleTableView(false)" :disabled="!graphViewAvailable"
                        :title="!graphViewAvailable ? 'Graph visualization not available' : ''">
                        <i class="fa-solid fa-share-nodes"></i>
                    </button>
                </div>
            </div>
        </div>
        <div v-if="queryResult[`${jobNameForVisualization}_${tableNameForVisualization}`]?.error">
            <div class="alert alert-danger mb-3" role="alert">
                <i class="fa fa-exclamation-triangle me-1"></i>
                {{ queryResult[`${jobNameForVisualization}_${tableNameForVisualization}`]?.error }}
            </div>
        </div>
        <div v-if="outputData">
            <div v-if="tableView">
                <div class="visualization-container">
                    <table class="table table-bordered visualization-table mb-0">
                        <thead>
                            <tr>
                                <th v-for="(column, index) in outputColumns" :key="index">{{ column.title }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in outputData.slice(0, previewRows)" :key="rowIndex">
                                <td v-for="(column, columnIndex) in outputColumns"
                                    :class="$store.state.darkMode ? '' : 'bg-white'" :key="`${rowIndex},${columnIndex}`"
                                    :title="row[column.data]"> {{ formatCellData(row[column.data]) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <small v-if="outputData.length > previewRows"
                    class="text-muted d-flex align-items-center justify-content-center mt-1">
                    <i class="fa fa-circle-info me-2"></i>
                    Showing {{ previewRows }} of {{ outputData.length }} rows. Export to view full results.
                </small>
            </div>
            <div v-else>
                <div v-if="graphViewAvailable">
                    <D3ForceGraph :data="graphData" :idrVisualiseMode="true"/>
                </div>
                <div v-else>
                    <div class="text-center py-4 rounded border border-dashed mb-3" :class="$store.state.darkMode ? 'dark-mode-bg' : 'bg-white'">
                        <div class="text-muted mb-2">
                            <i class="fa-solid fa-circle-exclamation" style="font-size: 1.5rem; opacity: 0.5;"></i>
                        </div>
                        <p class="text-muted mb-0">No data to visualize!</p>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <div v-if="queryRunning" class="text-center py-4 rounded border border-dashed" :class="$store.state.darkMode ? 'dark-mode-bg' : 'bg-white'">
                <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p class="text-muted mb-0">Loading visualization...</p>
            </div>
            <div v-else>
                <div class="text-center py-4 rounded border border-dashed" :class="$store.state.darkMode ? 'dark-mode-bg' : 'bg-white'">
                    <div class="text-muted mb-2">
                        <i class="fa-solid fa-circle-exclamation" style="font-size: 1.5rem; opacity: 0.5;"></i>
                    </div>
                    <p class="text-muted mb-0">No data to visualize!</p>
                </div>
            </div>
        </div>
    </div>
    <div class="d-flex align-items-center justify-content-end gap-2 pb-3">
        <button v-if="outputData?.length > 0" class="btn btn-secondary" @click="downloadResults" :disabled="exporting">
            <span v-if="exporting">
                <div class="spinner-border spinner-border-sm me-2" role="status" style="width: 1em; height: 1em;">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </span>
            Export
        </button>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import csv from '../../utils/tools/csv';
import inputField from '../../components/InputField.vue';
import D3ForceGraph from '../../components/D3ForceGraph.vue';
export default {
    name: 'Job Stack Deployment Visualization',
    components: { inputField, D3ForceGraph },
    props: [
        "loading",
        "deployment",
        "jobs",
        "fetchVisualizationTrigger",
        "queryRunning",
        "showJobTableTrigger",
        "queryId",
        "queryResult",
        "queryDetails",
        "jobNameForVisualization",
        "tableNameForVisualization",
        "limitForVisualization"
    ],
    data() {
        return {
            exporting: false,
            previewRows: 50,

            jobName: "",
            tableName: "",
            limit: null,
            tableView: true, // default to table view
        };
    },
    methods: {
        ...mapActions({
            fetchFullQueryFile: "amdp/fetchFullQueryFile",
        }),
        formatCellData(value) {
            if (!value) return "-";
            const str = String(value);
            return str.length > 50 ? str.substring(0, 50) + "..." : str;
        },
        async downloadResults() {
            this.exporting = true;
            try {
                // if partial result, get all results and download
                if (this.isPartialResult) {
                    const response = await this.fetchFullQueryFile(this.queryId);
                    if (response.https) {
                        const url = response.https;
                        const a = document.createElement("a");
                        a.href = url;
                        a.click();
                        window.URL.revokeObjectURL(url); // clean up the URL
                        a.remove(); // remove the element from the DOM
                    } else {
                        console.error("Failed to download results, Try again!");
                    }
                }
                // else download results immediately
                else {
                    let header = []
                    if (this.outputData.length > 0) {
                        Object.keys(this.outputData[0]).map((value) => header[value] = value)
                        csv.exportCSV(`${this.$store.state.currentClient.cid}_${this.pipelineJobConfig.name}_${this.jobNameForVisualization}_query`, [...header, ...this.outputData])
                    } else {
                        this.$store.dispatch("addToast", {
                            message: "No data to export",
                            type: "info",
                        });
                    }
                }
            } catch (error) {
                console.error("Error exporting results: ", error);
                this.$store.dispatch("addToast", {
                    message: "Failed to download results. Please retry or contact support.",
                    type: "error",
                });
            } finally {
                this.exporting = false;
            }
        },
        toggleTableView(value) {
            this.tableView = value;
        },
        /** Row keys follow API column names (often UPPERCASE); match logical names case-insensitively. */
        columnKeyFor(logicalName) {
            const want = String(logicalName).toLowerCase();
            const col = this.outputColumns.find((c) => String(c.data).toLowerCase() === want);
            return col ? col.data : null;
        },
        rowField(row, logicalName) {
            const key = this.columnKeyFor(logicalName);
            return key != null ? row[key] : undefined;
        },
        /**
         * Neighbor ids from ENTITY_NEIGHBORS / entity_neighbors:
         * - Array of objects ({ id } / { Id } / structs from Athena)
         * - Array of string ids
         * - JSON string of the above
         * - Bracket notation string: [{id=uuid, confidence=...}, ...]
         */
        parseNeighborIdsFromEntityNeighbors(value) {
            if (value == null) return [];

            if (typeof value === "object" && !Array.isArray(value)) {
                const id = value.id ?? value.Id ?? value.ID;
                return id != null && String(id).trim() !== "" ? [String(id).trim()] : [];
            }

            if (Array.isArray(value)) {
                const out = [];
                for (const n of value) {
                    if (n == null) continue;
                    if (typeof n === "string") {
                        const t = n.trim();
                        if (t) out.push(t);
                        continue;
                    }
                    if (typeof n === "object") {
                        const id = n.id ?? n.Id ?? n.ID;
                        if (id != null && String(id).trim() !== "") {
                            out.push(String(id).trim());
                        }
                    }
                }
                return out;
            }

            const s = String(value).trim();
            if (!s) return [];

            const looksLikeJson =
                (s.startsWith("[") && /"id"\s*:/.test(s)) ||
                (s.startsWith("{") && /"id"\s*:/.test(s));
            if (looksLikeJson) {
                try {
                    const parsed = JSON.parse(s);
                    return this.parseNeighborIdsFromEntityNeighbors(parsed);
                } catch (_) {
                    /* fall through to bracket / regex extraction */
                }
            }

            const ids = [];
            const reBracket = /\{id\s*=\s*([^,}]+)[,}]/gi;
            let m;
            while ((m = reBracket.exec(s)) !== null) {
                const id = m[1].trim();
                if (id) ids.push(id);
            }
            const reJsonId = /"id"\s*:\s*"([^"\\]+(?:\\.[^"\\]*)*)"/g;
            while ((m = reJsonId.exec(s)) !== null) {
                const id = m[1].replace(/\\"/g, '"').trim();
                if (id) ids.push(id);
            }
            return [...new Set(ids)];
        },
        /** neighbor_id may be a single string or a delimited list from some engines. */
        parseNeighborIdScalar(value) {
            if (value == null) return [];
            if (Array.isArray(value)) {
                return value
                    .map((v) => (v != null ? String(v).trim() : ""))
                    .filter(Boolean);
            }
            const s = String(value).trim();
            if (!s) return [];
            if (s.startsWith("[") && s.endsWith("]")) {
                try {
                    const parsed = JSON.parse(s);
                    return this.parseNeighborIdScalar(parsed);
                } catch (_) {
                    /* fall through */
                }
            }
            if (s.includes(",") && !s.includes("=")) {
                return s.split(",").map((p) => p.trim().replace(/^["']|["']$/g, "")).filter(Boolean);
            }
            return [s];
        },
        buildDeploymentGraphData(rows) {
            const kEntityId = this.columnKeyFor("entity_id");
            const kEntityType = this.columnKeyFor("entity_type");
            const kEntityDomain = this.columnKeyFor("entity_domain");
            const kNeighborType = this.columnKeyFor("entity_neighbor_type");
            const kNeighborDomain = this.columnKeyFor("entity_neighbor_domain");
            const kNeighborId = this.columnKeyFor("neighbor_id");
            const kEntityNeighbors = this.columnKeyFor("entity_neighbors");

            const nodesMap = new Map();
            const edges = [];
            const edgeKeySet = new Set();

            // KEY CHANGE: nodeKey now includes type, domain and key to prevent cross-domain ID collisions
            const nodeKey = (type, domain, key) => `${String(type).trim()}~${String(domain).trim()}~${String(key).trim()}`;

            const addNode = (id, type, domain) => {
                if (id == null || String(id).trim() === '') return;
                const t = type != null ? String(type).toLowerCase() : '';
                const d = domain != null ? String(domain) : '';
                const k = String(id).trim(); // raw ID for display
                const nk = nodeKey(t, d, k);           // <-- composite key. Eg, "type~domain~key(which is id)"
                if (!nodesMap.has(nk)) {
                    
                    nodesMap.set(nk, {
                        id: nk,                            // use composite key as node ID
                        type: t || 'unknown',
                        domain: d,
                        key: k,
                    });
                }
            };

            for (const row of rows) {
                const entityId = kEntityId != null ? row[kEntityId] : undefined;
                if (entityId == null || String(entityId).trim() === '') continue;

                const entityType = kEntityType != null ? row[kEntityType] : undefined;
                const entityDomain = kEntityDomain != null ? row[kEntityDomain] : undefined;
                const neighborType = kNeighborType != null ? row[kNeighborType] : undefined;
                const neighborDomain = kNeighborDomain != null ? row[kNeighborDomain] : undefined;

                addNode(entityId, entityType, entityDomain);

                const neighborIds = [];
                if (kNeighborId != null) {
                    const rawNid = row[kNeighborId];
                    if (rawNid != null && String(rawNid).trim() !== '') {
                        neighborIds.push(...this.parseNeighborIdScalar(rawNid));
                    }
                }
                if (kEntityNeighbors != null) {
                    const rawNeighbors = row[kEntityNeighbors];
                    if (rawNeighbors != null && !(typeof rawNeighbors === 'string' && rawNeighbors.trim() === '')) {
                        neighborIds.push(...this.parseNeighborIdsFromEntityNeighbors(rawNeighbors));
                    }
                }

                const uniqueTargets = [...new Set(neighborIds)];
                const srcKey = nodeKey(String(entityType).trim(), entityDomain, String(entityId).trim());  // <-- use composite key

                for (const nid of uniqueTargets) {
                    if (!nid) continue;
                    addNode(nid, neighborType, neighborDomain);
                    const tgtKey = nodeKey(neighborType, neighborDomain, nid);                // <-- composite key

                    if (tgtKey === srcKey) continue;                            // skip self-loops
                    const ek = `${srcKey}->${tgtKey}`;
                    if (!edgeKeySet.has(ek)) {
                        edgeKeySet.add(ek);
                        edges.push({ source: srcKey, target: tgtKey });         // <-- use composite keys
                    }
                }
            }

            if (nodesMap.size === 0) return null;
            return { nodes: [...nodesMap.values()], edges };
        },
    },
    computed: {
        pipelineJobConfig() {
            return Object.values(this.jobs).find(job => job.kind?.toLowerCase() === 'pipeline');
        },
        jobNameOptions() {
            let options = Object.keys(this.queryDetails).map(jobName => ({ label: jobName, value: jobName })) || [];
            return options.sort((a, b) => a.label.localeCompare(b.label));
        },
        tableNameOptions() {
            let options = this.queryDetails?.[this.jobName]?.tables?.map(tableName => ({ label: tableName, value: tableName })) || [];
            return options.sort((a, b) => a.label.localeCompare(b.label));
        },
        outputData() {
            return this.queryResult?.[`${this.jobNameForVisualization}_${this.tableNameForVisualization}`]?.data || null;
        },
        outputColumns() {
            return this.queryResult?.[`${this.jobNameForVisualization}_${this.tableNameForVisualization}`]?.columns || [];
        },
        isPartialResult() {
            return this.queryResult?.[`${this.jobNameForVisualization}_${this.tableNameForVisualization}`]?.isPartialResult || false;
        },
        graphViewAvailable() {
            const requiredFields = [
                "entity_id",
                "entity_type",
                "entity_domain",
                "entity_neighbor_type",
                "entity_neighbor_domain"
            ];

            if (!this.outputColumns || this.outputColumns.length === 0) return false;

            const hasRequiredFields = requiredFields.every((field) => this.columnKeyFor(field) != null);
            const hasNeighborField =
                this.columnKeyFor("entity_neighbors") != null || this.columnKeyFor("neighbor_id") != null;

            return hasRequiredFields && hasNeighborField;
        },
        graphData() {
            if (!this.graphViewAvailable || !this.outputData?.length) {
                return null;
            }
            return this.buildDeploymentGraphData(this.outputData);
        },

    },
    watch: {
        // only fetch if both fetchVisualizationTrigger and showJobTableTrigger are true. Only at this time, all necessary data is available to fetch the visualization.
        fetchVisualizationTrigger: {
            handler(newVal, oldVal) {
                if (newVal && oldVal !== undefined && newVal !== oldVal && this.showJobTableTrigger) {
                    this.$emit('visualizeTrigger');
                }
            },
            immediate: true,
        },
        showJobTableTrigger: {
            handler(newVal, oldVal) {
                if (newVal && oldVal !== undefined && newVal !== oldVal && this.fetchVisualizationTrigger) {
                    this.jobName = this.jobNameForVisualization;
                    this.tableName = this.tableNameForVisualization;
                    this.limit = this.limitForVisualization;
                    this.$emit('visualizeTrigger');
                }
            },
            immediate: false,
        },
        jobName: {
            handler(newVal, oldVal) {
                if (newVal && oldVal && newVal !== oldVal) {
                    this.$emit('updateJobName', newVal);
                    const firstTableInJob = this.queryDetails[newVal]?.tables?.[0] || "";
                    this.tableName = firstTableInJob;
                }
            },
            immediate: true,
        },
        tableName: {
            handler(newVal, oldVal) {
                if (newVal && oldVal && newVal !== oldVal) {
                    this.$emit('updateTableName', newVal);
                }
            },
            immediate: true,
        },
        limit: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal && newVal != null && newVal != undefined) {
                    this.$emit('updateLimit', newVal);
                }
            },
            immediate: true,
        },
    },
    beforeMount() {
        this.$emit('setVisualizationButton', true);
        if (this.fetchVisualizationTrigger) {
            this.$emit('visualizeTrigger');
        }

        // set the jobNameForVisualization and tableNameForVisualization
        this.jobName = this.jobNameForVisualization;
        this.tableName = this.tableNameForVisualization;
        this.limit = this.limitForVisualization;
    },
    beforeUnmount() {
        this.$emit('setVisualizationButton', false);
    },
    emits: ['setVisualizationButton', 'visualizeTrigger', 'updateJobName', 'updateTableName', 'updateLimit'],
}
</script>

<style scoped>
.visualization-container {
    width: 100%;
    overflow-x: auto;
    /* Optional: matches table border */
    border-radius: 4px;
    margin-bottom: 20px;
}

/* Ensure the table doesn't collapse and forces the container to scroll */
.visualization-table {
    width: 100%;
    min-width: 600px;
    /* Adjust based on your column count */
    table-layout: auto;
    white-space: nowrap;
    /* Prevents text from wrapping, forcing horizontal scroll */
}

.visualization-table> :not(caption)>*>*:not(th) {
    padding: 0.5rem 0.5rem;
    font-size: 0.8rem;
}

.visualization-table>*>th {
    font-size: 0.9rem;
    font-weight: bold;
    background-color: #f8f9fa;
    /* Keeps headers distinct while scrolling */
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;
.dark-mode-bg {
    background-color: $dark-card-bg !important;
}
</style>